import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CheckCircle2, XCircle, Send, IndianRupee, FileText, Copy } from "lucide-react";

// ============ COUPONS TAB ============
export function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "flat", discount_value: 100, usage_limit: 100, expires_at: "", event_id: "" });
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: e }] = await Promise.all([api.get("/admin/coupons"), api.get("/events")]);
      setItems(c); setEvents(e);
    } catch (err) { toast.error("Failed to load coupons"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/coupons", { ...form, event_id: form.event_id || null, expires_at: form.expires_at || null });
      toast.success("Coupon created");
      setForm({ code: "", discount_type: "flat", discount_value: 100, usage_limit: 100, expires_at: "", event_id: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    await load();
  };

  const now = new Date();
  const bucket = (c) => {
    if (c.status !== "active") return "expired";
    if (c.used_count >= c.usage_limit) return "used";
    if (c.expires_at && new Date(c.expires_at) < now) return "expired";
    return "active";
  };

  return (
    <div data-testid="admin-coupons-tab" className="space-y-6">
      <form onSubmit={create} className="glass rounded-3xl p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-coupon-form">
        <input required data-testid="coupon-code" placeholder="CODE (e.g. WELCOME10)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input-dark uppercase font-mono" />
        <select data-testid="coupon-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="input-dark">
          <option value="flat" className="bg-bg">Flat ₹ off</option>
          <option value="percent" className="bg-bg">Percent %</option>
        </select>
        <input required type="number" min="1" data-testid="coupon-value" placeholder="Discount value" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} className="input-dark" />
        <input type="number" min="1" data-testid="coupon-limit" placeholder="Usage limit" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: Number(e.target.value) })} className="input-dark" />
        <input type="date" data-testid="coupon-expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="input-dark" />
        <select data-testid="coupon-scope" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="input-dark">
          <option value="" className="bg-bg">All events</option>
          {events.map((ev) => <option key={ev.id} value={ev.id} className="bg-bg">{ev.title}</option>)}
        </select>
        <button disabled={saving} data-testid="coupon-create" className="btn-primary justify-center sm:col-span-2 lg:col-span-3">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Coupon
        </button>
      </form>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5">
              <tr>
                {["Code", "Type", "Value", "Used / Limit", "Scope", "Expires", "Status", ""].map((h) => (
                  <th key={h} className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const b = bucket(c);
                return (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`coupon-row-${c.code}`}>
                    <td className="p-3 font-mono uppercase font-bold text-lime">{c.code}</td>
                    <td className="p-3">{c.discount_type}</td>
                    <td className="p-3">{c.discount_type === "flat" ? `₹${c.discount_value}` : `${c.discount_value}%`}</td>
                    <td className="p-3">{c.used_count} / {c.usage_limit}</td>
                    <td className="p-3">{c.event_id ? (events.find((e) => e.id === c.event_id)?.title || c.event_id).slice(0, 24) : "All events"}</td>
                    <td className="p-3">{c.expires_at || "—"}</td>
                    <td className="p-3"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${b === "active" ? "bg-lime text-ink" : b === "used" ? "bg-orangex text-white" : "bg-white/10 text-muted"}`}>{b}</span></td>
                    <td className="p-3"><button onClick={() => remove(c.id)} data-testid={`coupon-delete-${c.code}`} className="text-magenta"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-muted font-mono text-xs">No coupons yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============ CAMPAIGNS TAB ============
export function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", image_path: "", channels: ["popup"], audience_city: "", audience_role: "", audience_interest: "", cta_url: "", cta_label: "Learn more" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/campaigns"); setItems(data); } catch { toast.error("Failed to load campaigns"); }
  };
  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads?kind=campaign", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const toggleChannel = (c) =>
    setForm((f) => ({ ...f, channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c] }));

  const send = async (e) => {
    e.preventDefault();
    if (form.channels.length === 0) return toast.error("Pick at least one channel");
    if (!window.confirm(`Broadcast this campaign now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/campaigns", form);
      toast.success(`Sent to ${data.audience_size} user(s) • ${data.email_sent} email(s)`);
      setForm({ title: "", message: "", image_path: "", channels: ["popup"], audience_city: "", audience_role: "", audience_interest: "", cta_url: "", cta_label: "Learn more" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-campaigns-tab" className="space-y-6">
      <form onSubmit={send} className="glass rounded-3xl p-5 space-y-3" data-testid="campaign-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input required data-testid="campaign-title" placeholder="Campaign title (shows as popup heading & email subject)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" />
          <input data-testid="campaign-cta-url" placeholder="CTA URL (optional)" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="input-dark" />
        </div>
        <textarea required rows={4} data-testid="campaign-message" placeholder="Message body" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-dark resize-none" />

        <div className="flex flex-wrap items-center gap-3">
          <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="campaign-image-upload">
            <FileText className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Attach Image"}
            <input type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files?.[0])} />
          </label>
          {form.image_path && <img src={form.image_path} alt="" className="w-16 h-10 object-cover rounded" data-testid="campaign-image-preview" />}
        </div>

        <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Channels</div>
        <div className="flex gap-2" data-testid="campaign-channels">
          {[["popup", "In-App Popup"], ["email", "Email"]].map(([k, l]) => (
            <button type="button" key={k} data-testid={`channel-${k}`} onClick={() => toggleChannel(k)}
              className={`px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full border ${form.channels.includes(k) ? "bg-lime text-ink border-lime" : "bg-white/5 border-white/10 text-muted"}`}>{l}</button>
          ))}
        </div>

        <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Audience filter (optional — leave blank for all)</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="campaign-city" placeholder="City" value={form.audience_city} onChange={(e) => setForm({ ...form, audience_city: e.target.value })} className="input-dark" />
          <select data-testid="campaign-role" value={form.audience_role} onChange={(e) => setForm({ ...form, audience_role: e.target.value })} className="input-dark">
            <option value="" className="bg-bg">All user types</option>
            <option value="user" className="bg-bg">Attendees only</option>
            <option value="organizer" className="bg-bg">Organizers only</option>
          </select>
          <select data-testid="campaign-interest" value={form.audience_interest} onChange={(e) => setForm({ ...form, audience_interest: e.target.value })} className="input-dark">
            <option value="" className="bg-bg">All interests</option>
            {["Events", "House Parties", "Trips", "After Parties", "Single", "In Relationship", "College Student"].map((i) => (
              <option key={i} value={i} className="bg-bg">{i}</option>
            ))}
          </select>
        </div>

        <button disabled={busy} data-testid="campaign-send" className="btn-primary w-full justify-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Broadcast Campaign
        </button>
      </form>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5">
              <tr>{["Title", "Channels", "Audience", "Popups", "Emails", "Created"].map((h) => <th key={h} className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-white/5" data-testid={`campaign-row-${c.id}`}>
                  <td className="p-3 font-bold">{c.title}</td>
                  <td className="p-3">{(c.channels || []).join(", ")}</td>
                  <td className="p-3">{c.audience_size ?? "—"}</td>
                  <td className="p-3">{c.popup_delivered_count ?? 0}</td>
                  <td className="p-3">{c.email_sent_count ?? 0}</td>
                  <td className="p-3 font-mono text-[10px]">{c.created_at?.slice(0, 16)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-muted font-mono text-xs">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============ KYC TAB ============
export function KycTab() {
  const [orgs, setOrgs] = useState([]);
  const [notes, setNotes] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/admin/organizers"); setOrgs(data); } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/organizers/${id}/kyc`, { decision, notes: notes[id] || "" });
      toast.success(`KYC ${decision}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="admin-kyc-tab" className="space-y-4">
      {orgs.map((o) => (
        <div key={o.id} className="glass rounded-3xl p-5" data-testid={`kyc-row-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
            <div>
              <div className="font-display uppercase text-xl leading-tight">{o.name}{o.brand_name ? ` · ${o.brand_name}` : ""}</div>
              <div className="text-[11px] font-mono text-muted mt-1">{o.email} {o.phone && <>· {o.phone}</>}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Completion: <span className="text-lime">{o.organizer_completion}%</span></div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${o.kyc_status === "approved" ? "bg-lime text-ink" : o.kyc_status === "rejected" ? "bg-magenta text-white" : "bg-orangex text-white"}`} data-testid={`kyc-status-${o.id}`}>{o.kyc_status || "pending"}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-xs font-mono">
            <div>GST: <span className="text-white">{o.gst_number || "—"}</span></div>
            <div>Aadhaar Name: <span className="text-white">{o.name_as_aadhaar || "—"}</span></div>
            <div>Bank: <span className="text-white">{o.bank_account_holder || "—"} · {o.bank_account_number ? `${o.bank_account_number.slice(-4).padStart(o.bank_account_number.length, "*")}` : "—"} · {o.bank_ifsc || "—"}</span></div>
            <div className="flex gap-3">
              {o.gst_doc_path && <a href={`${backend}/api/files/${o.gst_doc_path}`} target="_blank" rel="noreferrer" data-testid={`kyc-view-gst-${o.id}`} className="underline text-lime">View GST</a>}
              {o.aadhaar_doc_path && <a href={`${backend}/api/files/${o.aadhaar_doc_path}`} target="_blank" rel="noreferrer" data-testid={`kyc-view-aadhaar-${o.id}`} className="underline text-lime">View Aadhaar</a>}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input placeholder="Notes (optional)" value={notes[o.id] || ""} onChange={(e) => setNotes({ ...notes, [o.id]: e.target.value })} className="input-dark !py-2 !text-xs flex-1 min-w-[200px]" data-testid={`kyc-notes-${o.id}`} />
            <button onClick={() => decide(o.id, "approved")} data-testid={`kyc-approve-${o.id}`} className="btn-lime !py-2 !px-3 !text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
            <button onClick={() => decide(o.id, "rejected")} data-testid={`kyc-reject-${o.id}`} className="btn-ghost !py-2 !px-3 !text-xs !text-magenta"><XCircle className="w-3.5 h-3.5" /> Reject</button>
          </div>
        </div>
      ))}
      {orgs.length === 0 && <div className="glass rounded-3xl p-6 font-mono text-sm text-muted">No organisers yet.</div>}
    </div>
  );
}

// ============ PAYOUTS TAB ============
export function PayoutsTab() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/payouts"); setRows(data); } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const mark = async (r) => {
    const ref = window.prompt("Payment reference / UTR:", "");
    if (ref === null) return;
    try {
      await api.post("/admin/payouts/mark-sent", { organizer_id: r.organizer_id, event_id: r.event_id, amount: r.payout_due, reference: ref });
      toast.success("Marked as sent");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v || ""); toast.success("Copied"); };

  return (
    <div data-testid="admin-payouts-tab" className="glass rounded-3xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr>{["Organizer", "Event", "Tickets", "Gross", "Commission", "Payout Due", "Bank", "Status", ""].map((h) => <th key={h} className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.organizer_id}-${r.event_id}`} className="border-t border-white/5" data-testid={`payout-row-${r.organizer_id}-${r.event_id}`}>
                <td className="p-3"><div className="font-bold">{r.organizer_name || "—"}</div><div className="text-[10px] font-mono text-muted">{r.organizer_email}</div></td>
                <td className="p-3">{r.event_title || r.event_id}</td>
                <td className="p-3">{r.tickets_sold}</td>
                <td className="p-3">₹{r.gross}</td>
                <td className="p-3 text-magenta">− ₹{r.commission}</td>
                <td className="p-3 font-bold text-lime">₹{r.payout_due}</td>
                <td className="p-3 text-[10px] font-mono">
                  {r.bank?.account ? (
                    <button onClick={() => copy(`${r.bank.holder} • ${r.bank.account} • ${r.bank.ifsc}`)} className="underline hover:text-lime inline-flex items-center gap-1" data-testid={`copy-bank-${r.organizer_id}`}>
                      <Copy className="w-3 h-3" /> {r.bank.ifsc}
                    </button>
                  ) : "—"}
                </td>
                <td className="p-3"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${r.status === "sent" ? "bg-lime text-ink" : "bg-orangex text-white"}`}>{r.status}</span>{r.reference && <div className="text-[9px] font-mono text-muted mt-0.5">Ref: {r.reference}</div>}</td>
                <td className="p-3">
                  {r.status !== "sent" && (
                    <button onClick={() => mark(r)} data-testid={`payout-mark-${r.organizer_id}-${r.event_id}`} className="btn-lime !py-1.5 !px-2 !text-[10px]"><IndianRupee className="w-3 h-3" /> Mark Sent</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="9" className="p-6 text-center text-muted font-mono text-xs">No paid transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
